import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BehaviorSubject,
    Observable,
    catchError,
    merge,
    scan,
    tap,
} from 'rxjs';

import { hasErrors } from '@perpay-web/utils/errorHandlers';
import { emitOrTimeout } from '@perpay-web/utils/observable';

import {
    SIDE_EFFECT_COMPLETE_STATE,
    SIDE_EFFECT_ERROR_STATE,
    SIDE_EFFECT_INITIAL_STATE,
    SIDE_EFFECT_PENDING_STATE,
    composeSideEffects,
    useSideEffect,
} from './useSideEffect';

export const getIsUnrequested = (state) =>
    state.requestState === SIDE_EFFECT_INITIAL_STATE;

export const getIsLoading = (state) =>
    state.requestState === SIDE_EFFECT_PENDING_STATE;

export const getIsLoadingOrUnrequested = (state) =>
    getIsLoading(state) || getIsUnrequested(state);

export const getData = (state) => state.value;

export const getErrors = (state) => state.errors;

export const getIsComplete = (state) =>
    state.requestState === SIDE_EFFECT_COMPLETE_STATE;

export const getErrorData = (state) => state.rawErrorData;

/**
 * @param sideEffect
 * @param {Object} [options={initialValue: null}] - Configuration options
 * @param {*} [options.initialValue=null] - Initial value for the data state
 * @param {number} [options.timeout] - Optional timeout in milliseconds for the side effect
 * @param {function(response): void} [options.onSuccess] - Callback function to be called when the side effect completes successfully
 * @param {function(error): void} [options.onError] - Callback function to be called when the side effect encounters an error
 */
export const useDataModule = (sideEffect, options = { initialValue: null }) => {
    const {
        sideEffect$,
        state$: sideEffectState$,
        next,
        reset,
    } = useSideEffect((...args) => {
        const result$ = sideEffect(...args);
        return emitOrTimeout(result$, options.timeout);
    });
    // Leverage the useState hook to store the initial state
    // to guarantee that the initial state will not change
    // and re-create the dataModuleState$ memoized value.
    const [initialState] = useState({
        requestState: sideEffectState$.value.state,
        errors: sideEffectState$.value.errors,
        value: options.initialValue,
        rawErrorData: {},
    });
    const [state, setState] = useState(initialState);
    const dataModuleState$ = useMemo(
        () => new BehaviorSubject(initialState),
        [initialState],
    );

    const isComplete = getIsComplete(state);
    const errors = getErrors(state);
    const isError = hasErrors(errors);
    const data = getData(state);

    const onSuccess = useCallback(
        (response) => {
            if (options.onSuccess) {
                options.onSuccess(response);
            }
        },
        [options],
    );

    const onError = useCallback(
        (error) => {
            if (options.onError) {
                options.onError(error);
            }
        },
        [options],
    );

    const sideEffectSubscriptionRef = useRef(null);
    if (sideEffectSubscriptionRef.current === null) {
        sideEffectSubscriptionRef.current = sideEffect$
            .pipe(
                catchError((error, source$) => {
                    onError(error);
                    return source$;
                }),
            )
            .subscribe({
                next: onSuccess,
            });
    }
    useEffect(() => {
        const subscription = sideEffectSubscriptionRef.current;
        return () => subscription.unsubscribe();
    }, []);

    // We use a ref here for the subscription instead of a useEffect.
    // This is because a child's useEffects will run BEFORE a parent's useEffects.
    // In perpay-web, often a parent component will define a hook and then pass
    // a callback to a child component, like an onMount prop.
    // If we define this listener here in a useEffect, and pass the emit callback to a child,
    // then the listener will start listening in the parent useEffect AFTER the child's
    // has already fired its callback within its useEffect.
    // If we use a ref to initialize the listener only once, then we
    // start listening immediately when the component first renders, before
    // any of its children can call dataRequest.
    const subscriptionRef = useRef(null);
    if (subscriptionRef.current === null) {
        subscriptionRef.current = sideEffectState$
            .pipe(
                scan((prevState, nextState) => {
                    let { value } = nextState;
                    if (
                        [
                            SIDE_EFFECT_PENDING_STATE,
                            SIDE_EFFECT_ERROR_STATE,
                        ].includes(nextState.state)
                    ) {
                        value = prevState.value;
                    }

                    if (nextState.state === SIDE_EFFECT_INITIAL_STATE) {
                        value = options.initialValue;
                    }

                    return {
                        requestState: nextState.state,
                        errors: nextState.errors,
                        rawErrorData: nextState.rawErrorData,
                        value,
                    };
                }, initialState),
                tap(dataModuleState$),
            )
            .subscribe((nextState) => setState(nextState));
    }
    useEffect(() => {
        const subscription = subscriptionRef.current;
        return () => subscription.unsubscribe();
    }, []);

    const dataRequest = useCallback(
        (requestPayload) => {
            next(requestPayload);
        },
        [next],
    );
    const dataReset = useCallback(() => {
        reset();
    }, [reset]);

    return {
        state,
        dataRequest,
        dataReset,
        sideEffect$,
        state$: dataModuleState$,
        isLoading: getIsLoading(state),
        isComplete,
        errors,
        data,
        isError,
    };
};

const isSameDataModule = (first, second) =>
    first.sideEffect$ === second.sideEffect$;

/**
 * Take a list of data modules and compose their side-effects.
 * Usage:
 * ```
 * const composedDataModule = useDataModule(
 *   composeDataModuleSideEffects(
 *     dataModuleOne,
 *     dataModuleTwo));
 *
 * useMount(() => composedDataModule.dataRequest());
 * ```
 *
 * Usage with overrides:
 * ```
 * const composedDataModule = useDataModule(
 *   composeDataModuleSideEffects(
 *     dataModuleOne,
 *      dataModule2));
 * useMount(() => composedDataModule.dataRequest([
 *   {
 *      dataModule: dataModuleOne,
 *      dataRequest: () => dataModuleOne.dataRequest('specific value'),
 *   }
 * ]))
 * ```
 * @returns
 */
export const composeDataModuleSideEffects = (...dataModules) => {
    const composed = composeSideEffects(
        ...dataModules.map((dataModule) => dataModule.sideEffect$),
    );

    // We return a function because a side-effect is a function that returns an Observable.
    return (overrides = []) => {
        const composed$ = composed();

        // We want to subscribe to this Observable and initiate the requests
        // only after the composed$ Observable has been subscribed to and
        // it has started listening for the results.
        const request$ = new Observable((subscriber) => {
            dataModules.forEach((dataModule) => {
                const matchingOverride = overrides.find((override) =>
                    isSameDataModule(override.dataModule, dataModule),
                );
                if (matchingOverride) {
                    matchingOverride.dataRequest();
                    return;
                }

                dataModule.dataRequest();
            });

            // Let listeners know we're done.
            subscriber.complete();
        });

        // First start listening, then kick off the requests
        return merge(composed$, request$);
    };
};
